import { z } from 'zod';
import { CHARGER_CONNECTIVITY } from '../lib/chargerConnectivity.js';
/**
 * Network-submission wire shapes: installers / SPs / fleet managers
 * propose a location + its chargers; admins review and approve. One
 * submission model shared by the PUBLIC Turnstile-gated shop form and
 * the AUTHENTICATED admin-app form (SP / fleet personas).
 */
/**
 * Charger identity charset — the resident-register rule (the superset of
 * the SP 20-char rule), shared by every submitter surface AND the api
 * schemas so validation can't drift. The identity is the last path
 * segment of the unit's configured websocket URL.
 */
export const CHARGER_IDENTITY_PATTERN = '^[A-Za-z0-9_-]{1,50}$';
export const CHARGER_IDENTITY_RE = new RegExp(CHARGER_IDENTITY_PATTERN);
export const NetworkSubmissionRoleSchema = z.enum([
    'electrician',
    'sales_partner',
    'fleet_manager',
    'property_manager',
    'other',
]);
/** 'commercial' carries the workplace case (the Location enum has no
 *  'workplace'); residential is excluded — that's the resident flow. */
export const NetworkSubmissionLocationTypeSchema = z.enum([
    'fleet',
    'multifamily',
    'public',
    'commercial',
]);
export const NetworkSubmissionChargerSchema = z.object({
    identity: z.string().regex(CHARGER_IDENTITY_RE),
    vendor: z.string().max(100).nullable().optional(),
    model: z.string().max(100).nullable().optional(),
    connector_types: z.array(z.enum(['J1772', 'NACS', 'CCS1'])).min(1).max(8),
    pay_to_use: z.boolean().optional(),
    max_power_kw: z.number().min(0).max(1000).nullable().optional(),
});
export const NetworkSubmissionCreateBodySchema = z.object({
    job_plan: z.object({ power_class: z.enum(['AC', 'DC']), expected_install_date: z.string().date() }).optional(),
    contact: z.object({
        name: z.string().min(1).max(120),
        email: z.string().email().max(254),
        phone: z.string().max(32).nullable().optional(),
        role: NetworkSubmissionRoleSchema,
    }),
    location: z.object({
        name: z.string().min(1).max(120),
        address: z.object({
            street: z.string().min(1).max(200),
            city: z.string().min(1).max(100),
            state: z.string().min(1).max(100),
            zip: z.string().min(1).max(20),
            country: z.string().max(100).optional(),
        }),
        coordinates: z.object({ lat: z.number(), lng: z.number() }).nullable().optional(),
        type: NetworkSubmissionLocationTypeSchema,
    }),
    chargers: z.array(NetworkSubmissionChargerSchema).min(1).max(50),
    /** How every unit in this deployment connects (Warren, 2026-10-01).
     *  Required on create; the Api refuses a body without it. */
    connectivity: z.enum(CHARGER_CONNECTIVITY),
    /** Fleet-manager submissions: the fleet the chargers belong to,
     *  validated server-side against the caller's assigned fleets. */
    fleetId: z.string().nullable().optional(),
});
export const NetworkSubmissionStatusSchema = z.enum([
    'pending',
    'approved',
    'rejected',
    'needs_info',
]);
/** Admin projection — the review queue list + detail. */
export const NetworkSubmissionAdminSchema = z.object({
    id: z.string(),
    status: NetworkSubmissionStatusSchema,
    source: z.enum(['public', 'authenticated']),
    contact: z.object({
        name: z.string(),
        email: z.string(),
        phone: z.string().nullable().optional(),
        role: NetworkSubmissionRoleSchema,
    }),
    location: z.object({
        name: z.string(),
        address: z.object({
            street: z.string(),
            city: z.string(),
            state: z.string(),
            zip: z.string(),
            country: z.string().optional(),
        }),
        coordinates: z.object({ lat: z.number(), lng: z.number() }).nullable().optional(),
        type: NetworkSubmissionLocationTypeSchema,
    }),
    chargers: z.array(NetworkSubmissionChargerSchema),
    /** The deployment's recorded connectivity choice; null when it was
     *  registered before the choice existed. Also the needs_info resubmit
     *  prefill's value (the shop reads this projection). */
    connectivity: z.enum(CHARGER_CONNECTIVITY).nullable().optional(),
    duplicateFlags: z.array(z.object({
        identity: z.string(),
        kind: z.enum(['existing_charger', 'pending_submission']),
    })),
    tenantId: z.string().nullable().optional(),
    fleetId: z.string().nullable().optional(),
    submitterUserId: z.string().nullable().optional(),
    reviewerNote: z.string().nullable().optional(),
    reviewedAt: z.string().nullable().optional(),
    admittance: z.enum(['no_auth', 'password']).nullable().optional(),
    createdLocationId: z.string().nullable().optional(),
    createdChargerIds: z.array(z.string()),
    createdAt: z.string(),
});
/** The submitter's own view (list on the authenticated surface). */
export const NetworkSubmissionMineSchema = z.object({
    id: z.string(),
    status: NetworkSubmissionStatusSchema,
    locationName: z.string(),
    chargerCount: z.number(),
    reviewerNote: z.string().nullable().optional(),
    createdAt: z.string(),
});
//# sourceMappingURL=network-submissions.js.map